/**
 * The sound manifest.
 *
 * Every cue is a named slot with a procedural fallback, so the game sounds
 * complete before a single audio file exists and the wiring is proven long
 * before anyone records anything. Dropping in a real sample later means adding
 * a `file` to the slot — no code changes anywhere else.
 */

export type Channel = "sfx" | "ui" | "music";

export type SfxName =
  | "deal"
  | "place"
  | "slide"
  | "pass"
  | "invalid"
  | "select"
  | "deselect"
  | "trick"
  | "bombSmall"
  | "bombBig"
  | "jokerBomb"
  | "goOut"
  | "levelUp"
  | "coin"
  | "xp"
  | "button"
  | "nav"
  | "tick"
  | "matchFound"
  | "tribute"
  | "victory"
  | "defeat";

/** How a slot is synthesised until a recording replaces it. */
export type Shape =
  /** Filtered noise with a fast decay — paper, cloth, card edges. */
  | { kind: "noise"; duration: number; cutoff: number; sweep?: number; gain?: number }
  /** A pitched blip, optionally sliding. */
  | { kind: "tone"; freq: number; to?: number; duration: number; type?: OscillatorType; gain?: number }
  /** Several tones in sequence — fanfares and stings. */
  | { kind: "sequence"; steps: { freq: number; at: number; duration: number; type?: OscillatorType }[]; gain?: number }
  /** A low body plus noise: impacts. */
  | { kind: "impact"; freq: number; duration: number; noise: number; gain?: number };

export interface SfxSlot {
  readonly channel: Channel;
  /** Randomly chosen per play, so repeats do not machine-gun. */
  readonly shapes: readonly Shape[];
  /** Ignore repeats within this many ms. */
  readonly throttleMs?: number;
  /** Path to a recording, once one exists. */
  readonly file?: string;
}

const cardNoise = (cutoff: number, duration: number, gain = 0.5): Shape =>
  ({ kind: "noise", duration, cutoff, sweep: -0.6, gain });

export const SFX: Record<SfxName, SfxSlot> = {
  // Four variants so dealing twenty-seven cards is not one sound played
  // twenty-seven times.
  deal: {
    channel: "sfx",
    shapes: [
      cardNoise(2600, 0.07), cardNoise(3100, 0.06),
      cardNoise(2200, 0.08), cardNoise(3600, 0.055),
    ],
  },
  place: { channel: "sfx", shapes: [cardNoise(1800, 0.1, 0.6), cardNoise(1500, 0.12, 0.55)] },
  slide: { channel: "sfx", shapes: [cardNoise(4200, 0.09, 0.35)] },
  pass: { channel: "sfx", shapes: [{ kind: "tone", freq: 300, to: 220, duration: 0.11, type: "sine", gain: 0.22 }] },
  invalid: {
    channel: "ui",
    shapes: [{ kind: "sequence", gain: 0.3, steps: [
      { freq: 200, at: 0, duration: 0.07, type: "square" },
      { freq: 150, at: 0.07, duration: 0.1, type: "square" },
    ] }],
  },
  select: { channel: "ui", shapes: [{ kind: "tone", freq: 720, duration: 0.05, type: "triangle", gain: 0.16 }] },
  deselect: { channel: "ui", shapes: [{ kind: "tone", freq: 520, duration: 0.05, type: "triangle", gain: 0.13 }] },
  trick: { channel: "sfx", shapes: [cardNoise(1200, 0.22, 0.5)] },

  // Three tiers of bang. A four-card bomb should not sound like four kings.
  bombSmall: { channel: "sfx", shapes: [{ kind: "impact", freq: 150, duration: 0.34, noise: 0.55, gain: 0.7 }] },
  bombBig: { channel: "sfx", shapes: [{ kind: "impact", freq: 96, duration: 0.5, noise: 0.75, gain: 0.85 }] },
  jokerBomb: {
    channel: "sfx",
    shapes: [{ kind: "sequence", gain: 0.9, steps: [
      { freq: 90, at: 0, duration: 0.5, type: "sawtooth" },
      { freq: 180, at: 0.06, duration: 0.4, type: "square" },
      { freq: 360, at: 0.14, duration: 0.35, type: "triangle" },
    ] }],
  },

  goOut: {
    channel: "sfx",
    shapes: [{ kind: "sequence", gain: 0.5, steps: [
      { freq: 523, at: 0, duration: 0.12 },
      { freq: 659, at: 0.1, duration: 0.12 },
      { freq: 784, at: 0.2, duration: 0.2 },
    ] }],
  },
  levelUp: {
    channel: "sfx",
    shapes: [{ kind: "sequence", gain: 0.55, steps: [
      { freq: 523, at: 0, duration: 0.1 },
      { freq: 659, at: 0.08, duration: 0.1 },
      { freq: 784, at: 0.16, duration: 0.1 },
      { freq: 1047, at: 0.24, duration: 0.28 },
    ] }],
  },
  coin: {
    channel: "ui",
    shapes: [{ kind: "sequence", gain: 0.3, steps: [
      { freq: 988, at: 0, duration: 0.06 },
      { freq: 1319, at: 0.05, duration: 0.12 },
    ] }],
  },
  xp: { channel: "ui", shapes: [{ kind: "tone", freq: 880, duration: 0.04, type: "sine", gain: 0.12 }], throttleMs: 40 },
  button: { channel: "ui", shapes: [{ kind: "tone", freq: 440, duration: 0.045, type: "triangle", gain: 0.14 }] },
  nav: { channel: "ui", shapes: [{ kind: "tone", freq: 620, to: 780, duration: 0.08, type: "sine", gain: 0.14 }] },
  tick: { channel: "ui", shapes: [{ kind: "tone", freq: 1200, duration: 0.03, type: "square", gain: 0.1 }], throttleMs: 500 },
  matchFound: {
    channel: "sfx",
    shapes: [{ kind: "sequence", gain: 0.5, steps: [
      { freq: 659, at: 0, duration: 0.12 },
      { freq: 988, at: 0.11, duration: 0.22 },
    ] }],
  },
  tribute: { channel: "sfx", shapes: [{ kind: "tone", freq: 400, to: 600, duration: 0.24, type: "sine", gain: 0.3 }] },
  victory: {
    channel: "music",
    shapes: [{ kind: "sequence", gain: 0.6, steps: [
      { freq: 523, at: 0, duration: 0.14 },
      { freq: 659, at: 0.13, duration: 0.14 },
      { freq: 784, at: 0.26, duration: 0.14 },
      { freq: 1047, at: 0.39, duration: 0.45 },
    ] }],
  },
  defeat: {
    channel: "music",
    shapes: [{ kind: "sequence", gain: 0.45, steps: [
      { freq: 440, at: 0, duration: 0.18 },
      { freq: 370, at: 0.17, duration: 0.18 },
      { freq: 294, at: 0.34, duration: 0.5 },
    ] }],
  },
};

/** Which bang a bomb of this power deserves. */
export function bombSound(power: number): SfxName {
  if (power >= 1000) return "jokerBomb";
  return power >= 60 ? "bombBig" : "bombSmall";
}
